import React from "react";
import { MOVE_MODE_ICONS, MoveMode } from "./MoveIcons";
import { t } from "../../utils/i18n";
import { relativeDayLabel } from "../../utils/format";

export interface MoveTrip {
  id: string;
  title: string;
  move_mode: MoveMode;
  location_name: string;
  destination_name: string;
  event_date: string;
  max_places: number;
  current_places: number;
  price: number;
  is_free: boolean;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  author_name: string;
  author_avatar?: string | null;
  user_id: string;
  confirmed_participants?: { id: string; name: string; avatar_url?: string | null; on_my_way?: boolean }[];
}

export const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Fallback français brut — utilisé uniquement là où `lang` n'est pas disponible (tables au niveau
// module, ex. KIND_BADGE d'Explore.tsx). Partout où `lang` est en portée (JSX), préférer
// `t(lang, MOVE_MODE_I18N_KEY[mode])`, déjà traduit dans les 12 langues (utils/i18n.ts).
export const moveModeLabel = (mode: MoveMode) => ({ ride: "Voiture", bike: "Vélo", walk: "Marche", scoot: "Trottinette" }[mode] || mode);
export const MOVE_MODE_I18N_KEY: Record<MoveMode, string> = { ride: "move_mode_ride", bike: "move_mode_bike", walk: "move_mode_walk", scoot: "move_mode_scoot" };

// Pile d'avatars des voyageurs + compteur de places — copie exacte du motif déjà utilisé sur les
// cartes de publication (PostCard.tsx, ~ligne 817-831) : mêmes tailles/marges/seuils, pour que
// MOVE se lise comme le reste d'OLTODO plutôt que comme un module à part visuellement.
export function AvatarStack({ participants, current, max, lang }: { participants?: MoveTrip["confirmed_participants"]; current: number; max: number; lang: string }) {
  const full = current >= max;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      {(participants || []).length > 0 && (
        <div style={{ display: "flex" }}>
          {(participants || []).slice(0, 3).map((pt, i) => (
            <div key={pt.id} style={{ position: "relative", width: 24, height: 24, borderRadius: "50%", border: "2px solid white", marginLeft: i > 0 ? -8 : 0, backgroundColor: "#333", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 9, fontWeight: 700 }}>
              {pt.avatar_url ? <img src={pt.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(pt.name)}
              {pt.on_my_way && <div style={{ position: "absolute", bottom: -1, right: -1, width: 8, height: 8, borderRadius: "50%", backgroundColor: "#2ecc71", border: "1.5px solid white" }} />}
            </div>
          ))}
        </div>
      )}
      <span style={{ backgroundColor: full ? "#c0392b" : "#F0F0F0", color: full ? "white" : "#0a0a0a", fontSize: 11, fontWeight: 700, borderRadius: 20, padding: "3px 9px" }}>
        {full ? t(lang, "move_full") : `${current}/${max} ${t(lang, "move_seats_suffix")}`}
      </span>
    </div>
  );
}

export default function MoveCard({ move, lang, onClick }: { move: MoveTrip; lang: string; onClick: () => void }) {
  const Icon = MOVE_MODE_ICONS[move.move_mode] || MOVE_MODE_ICONS.ride;
  const showVehicle = move.move_mode === "ride" && move.vehicle_make;
  return (
    <div onClick={onClick} style={{ display: "flex", gap: 12, padding: "14px 0", borderBottom: "1px solid #F5F5F5", cursor: "pointer" }}>
      <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#F0F0F0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={19} color="#0a0a0a" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {move.location_name} → {move.destination_name}
        </div>
        <div style={{ fontSize: 12.5, color: "#666", marginTop: 2 }}>
          {move.event_date && new Date(move.event_date).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
          {relativeDayLabel(move.event_date, lang) ? ` · ${relativeDayLabel(move.event_date, lang)}` : ""}
          {showVehicle ? ` · ${[move.vehicle_make, move.vehicle_model].filter(Boolean).join(" ")}` : ""}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0a0a0a" }}>{move.is_free ? (t(lang, "free") || "Gratuit") : `${move.price}€/place`}</span>
          <AvatarStack participants={move.confirmed_participants} current={move.current_places} max={move.max_places} lang={lang} />
        </div>
      </div>
    </div>
  );
}
